import { isUserProjectFinished } from '@internal/components/experience/utils/project-finish-deps';
import { Education, UserProject } from '@internal/types/experience';
import { Profile, Skill } from '@internal/types/profile';

import { MIN_PROJECTS_COUNT } from '../experience-highlights-section';
import { ProfileSectionId } from '../user-profile.interfaces';

export type MissingFieldKey =
  | 'firstName'
  | 'lastName'
  | 'job_search_status'
  | 'english_level'
  | 'experience_since'
  | 'seniority'
  | 'specialization'
  | 'technologies'
  | 'availability_type'
  | 'available_hours_per_week'
  | 'country'
  | 'linkedin'
  | 'readyToStartWhen'
  | 'experience'
  | 'education'
  | 'timeZone'
  | 'workingHoursFrom'
  | 'workingHoursTo'
  | 'min_rate'
  | 'max_rate';

export interface MissingFieldDef {
  key: MissingFieldKey;
  sectionId: ProfileSectionId;
}

/** DOM id for a scalar field anchor. */
export const missingFieldTargetId = (key: MissingFieldKey) =>
  `missing-field-${key}`;

/** DOM id for a single incomplete project's "Details missing" pill. */
export const missingProjectTargetId = (projectId: string) =>
  `missing-field-experience-${projectId}`;

/** DOM id for a single incomplete education card. */
export const missingEducationTargetId = (educationId: string) =>
  `missing-field-education-${educationId}`;

export const isEducationFinished = (education: Education): boolean =>
  [
    education.university,
    education.field_of_study,
    education.degree,
    education.graduation_year,
  ].every(value => Boolean(String(value ?? '').trim()));

export const PROFILE_FIELD_ORDER: MissingFieldDef[] = [
  { key: 'firstName', sectionId: 'about' },
  { key: 'lastName', sectionId: 'about' },
  { key: 'job_search_status', sectionId: 'about' },
  { key: 'english_level', sectionId: 'about' },
  { key: 'country', sectionId: 'about' },
  { key: 'linkedin', sectionId: 'about' },
  { key: 'experience_since', sectionId: 'skills-and-specialisations' },
  { key: 'seniority', sectionId: 'skills-and-specialisations' },
  { key: 'specialization', sectionId: 'skills-and-specialisations' },
  { key: 'technologies', sectionId: 'skills-and-specialisations' },
  { key: 'availability_type', sectionId: 'project-preferences' },
  { key: 'available_hours_per_week', sectionId: 'project-preferences' },
  { key: 'min_rate', sectionId: 'project-preferences' },
  { key: 'max_rate', sectionId: 'project-preferences' },
  { key: 'readyToStartWhen', sectionId: 'project-preferences' },
  { key: 'experience', sectionId: 'experience' },
  { key: 'education', sectionId: 'education-tab' },
  { key: 'timeZone', sectionId: 'location' },
  { key: 'workingHoursFrom', sectionId: 'location' },
  { key: 'workingHoursTo', sectionId: 'location' },
];

export interface MissingFieldContext {
  profile: Profile;
  projects: UserProject[];
  projectsIsLoading: boolean;
  educations: Education[];
  educationsIsLoading: boolean;
  skills?: Skill[];
  skillsIsLoading?: boolean;
}

const isBlank = (value: unknown) =>
  value === null || value === undefined || String(value).trim() === '';

const isUnset = (value: unknown) => isBlank(value) || value === 'Unknown';

export const incompleteProfileSectionIds = (
  context: MissingFieldContext
): ProfileSectionId[] => {
  const ids = new Set<ProfileSectionId>();
  PROFILE_FIELD_ORDER.forEach(def => {
    if (isProfileFieldMissing(def.key, context)) {
      ids.add(def.sectionId);
    }
  });
  return [...ids];
};

export const missingProfileFieldCount = (
  context: MissingFieldContext
): number =>
  PROFILE_FIELD_ORDER.filter(def => isProfileFieldMissing(def.key, context))
    .length;

export const isProfileFieldMissing = (
  key: MissingFieldKey,
  {
    profile,
    projects,
    projectsIsLoading,
    educations,
    educationsIsLoading,
    skills = [],
    skillsIsLoading = false,
  }: MissingFieldContext
): boolean => {
  switch (key) {
    case 'firstName':
      return isBlank(profile.firstName);
    case 'lastName':
      return isBlank(profile.lastName);
    case 'job_search_status':
      return isBlank(profile.job_search_status);
    case 'english_level':
      return isBlank(profile.en_spoken);
    case 'linkedin':
      return isBlank(profile.linkedin);
    case 'experience_since':
      return isBlank(profile.experience_since);
    case 'seniority':
      return isBlank(profile.seniority);
    case 'specialization':
      return isBlank(profile.main_specialization);
    case 'technologies':
      return !skillsIsLoading && !skills.some(skill => skill.main);
    case 'country':
      return isBlank(profile.country);
    case 'availability_type':
      return isUnset(profile.availability_type);
    case 'available_hours_per_week':
      return isUnset(profile.available_hours_per_week);
    case 'readyToStartWhen':
      return isBlank(profile.readyToStartWhen);
    case 'timeZone':
      return isBlank(profile.timeZone);
    case 'workingHoursFrom':
      return isBlank(profile.workingHoursFrom);
    case 'workingHoursTo':
      return isBlank(profile.workingHoursTo);
    case 'min_rate':
      return !profile.min_rate_per_hour;
    case 'max_rate':
      return !profile.max_rate_per_hour;
    // Deliberately stricter than the backend (which needs only 4 complete
    // projects / 1 complete education): we encourage candidates to finish
    // every entry they added, so any unfinished draft counts as missing.
    case 'experience':
      return (
        !projectsIsLoading &&
        (projects.length < MIN_PROJECTS_COUNT ||
          projects.some(project => !isUserProjectFinished(project)))
      );
    case 'education':
      return (
        !educationsIsLoading &&
        (!educations.some(education => isEducationFinished(education)) ||
          educations.some(education => !isEducationFinished(education)))
      );
    default:
      return false;
  }
};
